import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
  IconButton,
  Link,
  CircularProgress,
  Alert
} from '@mui/material';
import { Location, CloseCircle, Copy } from 'iconsax-react';

interface LocationPopupProps {
  open: boolean;
  onClose: () => void;
  sessionData?: {
    fromName?: string;
    key?: string;
  };
}

interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

const LocationPopup: React.FC<LocationPopupProps> = ({
  open,
  onClose,
  sessionData
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState<LocationData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Generate Google Maps link
  const getGoogleMapsLink = (lat: number, lng: number) => {
    return `https://www.google.com/maps?q=${lat},${lng}`;
  };

  // Copy coordinates to clipboard
  const copyCoordinates = async () => {
    if (!location) return;
    
    const coordinates = `${location.latitude}, ${location.longitude}`;
    try {
      await navigator.clipboard.writeText(coordinates);
      // Simple feedback - could be enhanced with a toast notification
      console.log('Coordinates copied to clipboard');
    } catch (err) {
      console.error('Failed to copy coordinates:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = coordinates;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        console.log('Coordinates copied to clipboard (fallback method)');
      } catch (fallbackErr) {
        console.error('Failed to copy coordinates with fallback method:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  // Fetch user location
  const fetchLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        });
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setError('Location access denied by user.');
            break;
          case error.POSITION_UNAVAILABLE:
            setError('Location information is unavailable.');
            break;
          case error.TIMEOUT:
            setError('Location request timed out.');
            break;
          default:
            setError('An unknown error occurred while retrieving location.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  // Fetch location when popup opens
  useEffect(() => {
    if (open && !location && !loading && !error) {
      fetchLocation();
    }
  }, [open, location, loading, error]);

  // Reset state when popup closes
  useEffect(() => {
    if (!open) {
      setLocation(null);
      setError(null);
      setLoading(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: '450px'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: theme.palette.info.lighter
              }}
            >
              <Location size={20} color={theme.palette.info.main} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                User Location
              </Typography>
              {sessionData?.fromName && (
                <Typography variant="caption" color="text.secondary">
                  Requested from: {sessionData.fromName}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseCircle size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 1 }}>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Getting user location...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {location && (
          <Box sx={{ py: 1 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Coordinates:
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 2,
                backgroundColor: theme.palette.grey[50],
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`
              }}>
                <Typography variant="body1" fontFamily="monospace" sx={{ flex: 1 }}>
                  {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                </Typography>
                <IconButton size="small" onClick={copyCoordinates} title="Copy coordinates">
                  <Copy size={16} />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Open in Maps:
              </Typography>
              <Link
                href={getGoogleMapsLink(location.latitude, location.longitude)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 2,
                  backgroundColor: theme.palette.primary.lighter,
                  borderRadius: 1,
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                  }
                }}
              >
                <Location size={16} color={theme.palette.primary.main} />
                <Typography variant="body2" color="primary.main" fontWeight={500}>
                  View on Google Maps
                </Typography>
              </Link>
            </Box>

            <Box sx={{ 
              p: 2, 
              backgroundColor: theme.palette.grey[50], 
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Accuracy:</strong> ±{Math.round(location.accuracy)}m<br />
                <strong>Retrieved:</strong> {new Date(location.timestamp).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {error && (
          <Button
            onClick={fetchLocation}
            variant="outlined"
            disabled={loading}
            startIcon={<Location size={16} />}
          >
            Retry
          </Button>
        )}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationPopup;
